import { Button, Collapse, Input, Message, Modal } from '@arco-design/web-react';
import {
	BlockManager,
	IBlockData,
	JsonToMjml,
	t,
} from '@bay-email/core';
import {
	useBlock,
	useFocusIdx,
	useEditorContext,
	useEditorProps,
} from '@bay-email/editor';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CodemirrorEditor from '@extensions/components/Form/CodemirrorEditor';
import { BsChevronDown } from 'react-icons/bs';
import styles from './index.module.scss';

type LineHeightUnit = 'ratio' | 'px' | 'percent' | 'normal';

function trimTrailingZeros(value: number) {
	return value
		.toString()
		.replace(/\.0+$/, '')
		.replace(/(\.\d*?)0+$/, '$1');
}

function parsePixelValue(value?: unknown) {
	if (value === null || value === undefined) return undefined;
	const normalizedValue = String(value).trim().toLowerCase();
	const match = normalizedValue.match(/^-?\d*\.?\d+(px)?$/);
	if (!match) return undefined;

	const numericValue = Number(normalizedValue.replace(/px$/, ''));
	if (Number.isNaN(numericValue)) return undefined;

	return numericValue;
}

function parseLineHeight(value?: unknown): { amount: string; unit: LineHeightUnit } {
	const normalizedValue = String(value || '').trim();

	if (!normalizedValue) {
		return { amount: '', unit: 'ratio' };
	}

	if (normalizedValue === 'normal') {
		return { amount: '', unit: 'normal' };
	}

	if (/^-?\d*\.?\d+px$/i.test(normalizedValue)) {
		return { amount: normalizedValue.replace(/px$/i, ''), unit: 'px' };
	}

	if (/^-?\d*\.?\d+%$/.test(normalizedValue)) {
		return { amount: normalizedValue.replace(/%$/, ''), unit: 'percent' };
	}

	return { amount: normalizedValue, unit: 'ratio' };
}

function formatLineHeightValue(amount: string, unit: LineHeightUnit) {
	const normalizedAmount = amount.trim();

	if (unit === 'normal') {
		return 'normal';
	}

	if (!normalizedAmount) {
		return undefined;
	}

	if (unit === 'px') {
		return `${normalizedAmount}px`;
	}

	if (unit === 'percent') {
		return `${normalizedAmount}%`;
	}

	return normalizedAmount;
}

function enforceLineHeightMinimum(
	amount: string,
	unit: LineHeightUnit,
	fontSizePx: number,
) {
	const normalizedAmount = amount.trim();
	if (!normalizedAmount || unit === 'normal') {
		return { amount: normalizedAmount, adjusted: false };
	}

	const numericValue = Number(normalizedAmount);
	if (Number.isNaN(numericValue)) {
		return { amount: normalizedAmount, adjusted: false };
	}

	const minValue =
		unit === 'px' ? fontSizePx : unit === 'percent' ? 100 : unit === 'ratio' ? 1 : 0;

	if (numericValue >= minValue) {
		return { amount: normalizedAmount, adjusted: false };
	}

	return {
		amount: trimTrailingZeros(minValue),
		adjusted: true,
	};
}

function normalizeLineHeightInBlock(
	block: IBlockData,
	inheritedFontSizePx: number,
): boolean {
	let adjusted = false;
	const attributes = (block.attributes || {}) as Record<string, any>;
	const ownFontSizePx = parsePixelValue(attributes['font-size']);
	const activeFontSizePx = ownFontSizePx || inheritedFontSizePx;

	const rawLineHeight = attributes['line-height'];
	if (
		rawLineHeight !== undefined &&
		rawLineHeight !== null &&
		String(rawLineHeight).trim()
	) {
		const parsedLineHeight = parseLineHeight(rawLineHeight);
		const enforced = enforceLineHeightMinimum(
			parsedLineHeight.amount,
			parsedLineHeight.unit,
			activeFontSizePx,
		);

		if (enforced.adjusted) {
			const nextLineHeight = formatLineHeightValue(
				enforced.amount,
				parsedLineHeight.unit,
			);
			block.attributes = {
				...attributes,
				...(nextLineHeight ? { 'line-height': nextLineHeight } : {}),
			} as any;
			adjusted = true;
		}
	}

	if (Array.isArray(block.children) && block.children.length > 0) {
		block.children.forEach(child => {
			if (normalizeLineHeightInBlock(child as IBlockData, activeFontSizePx)) {
				adjusted = true;
			}
		});
	}

	return adjusted;
}

export function SourceCodePanel({
	jsonReadOnly,
}: {
	jsonReadOnly: boolean;
	// Accepted for API compatibility but no longer used: the MJML panel is now
	// always read-only (MJML is a lossy compile target — edit via JSON instead).
	mjmlReadOnly?: boolean;
}) {
	const { setValueByIdx, focusBlock, values } = useBlock();
	const { focusIdx } = useFocusIdx();

	// MJML is read-only reference text (a lossy compile target), so it only needs
	// to mirror the focused block — no dirty/error tracking.
	const [mjmlText, setMjmlText] = useState('');

	// JSON is the lossless, primary advanced-editing surface: it round-trips the
	// editor's native block representation exactly, unlike MJML (a reverse-compile
	// that can drop attributes/structure). It gets the same edit/apply UX.
	const [jsonText, setJsonText] = useState('');
	const [jsonDirty, setJsonDirty] = useState(false);
	const [jsonError, setJsonError] = useState<string | null>(null);
	// Roomy full-size editing surface — the inline box is cramped for real edits.
	const [jsonModalOpen, setJsonModalOpen] = useState(false);

	const { pageData } = useEditorContext();
	const { mergeTags, realUserProfile } = useEditorProps();
	const pageFontSizePx = useMemo(() => {
		return parsePixelValue((values as any)?.content?.data?.value?.['font-size']) || 14;
	}, [values]);

	const code = useMemo(() => {
		if (!focusBlock) return '';
		return JSON.stringify(focusBlock, null, 2) || '';
	}, [focusBlock]);

	// Apply the current JSON text to the focused block. Lossless: the parsed
	// object IS the editor's native block shape, so a valid paste reproduces the
	// design exactly. On failure we keep the user's text and show the error
	// instead of replacing (and breaking) the existing block.
	// Returns true when the text was successfully applied.
	const commitJson = useCallback(
		(rawText: string): boolean => {
			if (jsonReadOnly) return false;

			let parseValue: IBlockData;
			try {
				// Safely parse JSON instead of using eval
				parseValue = JSON.parse(rawText) as IBlockData;
			} catch (error: any) {
				setJsonError(
					error?.message
						? `${t('Invalid JSON')}: ${error.message}`
						: t('Invalid JSON. Check for syntax errors.'),
				);
				return false;
			}

			const block = BlockManager.getBlockByType(parseValue?.type);
			if (!block) {
				setJsonError(
					t('Unknown block "type". The JSON must describe a valid block.'),
				);
				return false;
			}
			if (
				!parseValue.data ||
				!parseValue.data.value ||
				!parseValue.attributes ||
				!Array.isArray(parseValue.children)
			) {
				setJsonError(
					t(
						'Incomplete block: JSON must include "type", "data.value", "attributes", and a "children" array.',
					),
				);
				return false;
			}

			if (normalizeLineHeightInBlock(parseValue, pageFontSizePx)) {
				Message.warning(t('Line height adjusted to minimum allowed values.'));
			}

			setValueByIdx(focusIdx, parseValue);
			setJsonError(null);
			setJsonDirty(false);
			return true;
		},
		[focusIdx, jsonReadOnly, pageFontSizePx, setValueByIdx],
	);

	const onChangeJsonText = useCallback((value: string) => {
		setJsonText(value);
		setJsonDirty(true);
		setJsonError(null);
	}, []);

	const onApplyJson = useCallback(() => {
		const applied = commitJson(jsonText);
		if (applied) {
			Message.success(t('JSON applied'));
		}
	}, [commitJson, jsonText]);

	// Apply from the modal: on success, close it so the user sees the result.
	const onApplyJsonFromModal = useCallback(() => {
		const applied = commitJson(jsonText);
		if (applied) {
			Message.success(t('JSON applied'));
			setJsonModalOpen(false);
		}
	}, [commitJson, jsonText]);

	// Switching to a different block discards any unapplied JSON edits for the
	// previous block and resyncs from the newly focused block below.
	useEffect(() => {
		setJsonDirty(false);
		setJsonError(null);
	}, [focusIdx]);

	// MJML is read-only (it's a lossy compile target), so always mirror the
	// focused block's generated MJML for reference/copy.
	useEffect(() => {
		if (!focusBlock) return;

		// dataSource.user is mergeTags.user — real overrides merged over hardcoded
		// sample data, so a real field left blank still reads the sample value at
		// that key. realUserProfile carries ONLY real fields — see MjmlDomRender.tsx,
		// which this mirrors so the Source Code tab doesn't leak sample data the
		// live canvas next to it already correctly avoids.
		const sourceDataSource = cloneDeep(mergeTags) || {};
		sourceDataSource.realUserProfile = realUserProfile;

		setMjmlText(
			JsonToMjml({
				idx: focusIdx,
				data: focusBlock,
				context: pageData,
				mode: 'production',
				dataSource: sourceDataSource,
			}),
		);
	}, [focusBlock, focusIdx, pageData, mergeTags, realUserProfile]);

	// Resync JSON text from the focused block unless the user has pending edits.
	useEffect(() => {
		if (!focusBlock) return;
		if (!jsonDirty) {
			setJsonText(code);
			setJsonError(null);
		}
	}, [focusBlock, code, jsonDirty]);

	if (!focusBlock) return null;

	return (
		<>
		<Collapse
			className='pt-3'
			expandIconPosition='right'
			expandIcon={<BsChevronDown size='1rem' />}
		>
			<Collapse.Item
				name='json'
				header='JSON Source'
				contentStyle={{ padding: '8px 13px' }}
			>
				{jsonReadOnly ? (
					<Input.TextArea
						key={code}
						value={jsonText}
						autoSize={{ maxRows: 25 }}
						readOnly
						className={styles.customTextArea}
					/>
				) : (
					<div className={styles.mjmlEditor}>
						<div className={styles.codeEditorShell}>
							<CodemirrorEditor
								value={jsonText}
								onChange={onChangeJsonText}
								refreshKey={`json-${focusIdx}`}
								mode='application/json'
							/>
						</div>
						{jsonError ? (
							<div className={styles.mjmlError} role='alert'>
								{jsonError}
							</div>
						) : null}
						<div className={styles.mjmlActions}>
							<Button
								size='small'
								onClick={() => setJsonModalOpen(true)}
							>
								{t('Expand editor')}
							</Button>
							<Button
								size='small'
								type='primary'
								disabled={!jsonDirty}
								onClick={onApplyJson}
							>
								{t('Apply JSON')}
							</Button>
						</div>
					</div>
				)}
			</Collapse.Item>
			<Collapse.Item
				name='mjml'
				header='MJML Source'
				contentStyle={{ padding: '8px 13px' }}
			>
				{/* MJML is a compiled output format and editing it back is lossy, so
				    this panel is read-only (view/copy for reference). Make edits via
				    the JSON Source above, which round-trips losslessly. */}
				<Input.TextArea
					key={code}
					value={mjmlText}
					autoSize={{ maxRows: 25 }}
					readOnly
					className={styles.customTextArea}
				/>
			</Collapse.Item>
		</Collapse>

		<Modal
			title={t('Edit JSON Source')}
			visible={jsonModalOpen}
			onCancel={() => setJsonModalOpen(false)}
			style={{ width: '90vw', maxWidth: 'none', top: 20 }}
			footer={
				<div className={styles.modalFooter}>
					<Button onClick={() => setJsonModalOpen(false)}>{t('Close')}</Button>
					<Button
						type='primary'
						disabled={!jsonDirty}
						onClick={onApplyJsonFromModal}
					>
						{t('Apply JSON')}
					</Button>
				</div>
			}
		>
			<div className={styles.modalEditor}>
				<div className={styles.modalEditorShell}>
					<CodemirrorEditor
						value={jsonText}
						onChange={onChangeJsonText}
						refreshKey={`json-modal-${focusIdx}-${jsonModalOpen}`}
						mode='application/json'
					/>
				</div>
				{jsonError ? (
					<div className={styles.mjmlError} role='alert'>
						{jsonError}
					</div>
				) : null}
			</div>
		</Modal>
		</>
	);
}
